'use client';

/**
 * Live insets for the "learning-cycle" reference screen.
 * OWNED BY THE INSETS STAGE — see ../index.tsx for the contract.
 *
 * Covers (reference pixels, 1672×941):
 *   - detail panel [1283,187,344,600]: ring, "текущий шаг", title, text, three bullets;
 *   - node 3 mute disc + selected-step ring; label 3 muted / selected label lime;
 *   - controller pill [497,829,678,68]: pause (local), status, next (cycleStep + 1).
 * The lime arc segments, the painted numbers and the history rings stay as painted.
 */

import { useContext, useEffect, useRef, useState, type CSSProperties } from 'react';
import { LivingContext } from '../../context';
import { SCENE_CANVAS, rectStyle, type Bilingual, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './learning-cycle.module.css';

const CANVAS = SCENE_CANVAS['learning-cycle'];
const at = (rect: Rect): CSSProperties => rectStyle(rect, CANVAS);
const stagger = (index: number): CSSProperties => ({ '--i': Math.min(index, 5) } as CSSProperties);

interface CycleStep {
  title: Bilingual;
  text: Bilingual;
  bullets: readonly [Bilingual, Bilingual, Bilingual];
}

const STEPS: readonly CycleStep[] = [
  {
    title: { ru: 'Цель учителя', en: 'Teacher goal' },
    text: {
      ru: 'Учитель задаёт направление: какой результат важен и зачем он нужен ученику. От этого зависят горизонт, глубина и формат работы.',
      en: 'The teacher sets the direction: which result matters and why it matters for the learner. Horizon, depth and format follow from it.',
    },
    bullets: [
      { ru: 'Понятная цель, а не список тем', en: 'A clear goal, not a topic list' },
      { ru: 'Решение остаётся за педагогом', en: 'The decision stays with the teacher' },
      { ru: 'Задаёт рамку для всего цикла', en: 'Frames the whole cycle' },
    ],
  },
  {
    title: { ru: 'Текущее состояние', en: 'Current state' },
    text: {
      ru: 'Система собирает, что ученик уже освоил, где сомневается и какие ошибки повторяются. Это точка отсчёта, а не оценка.',
      en: 'The system gathers what the learner has mastered, where they hesitate and which errors repeat. It is a starting point, not a grade.',
    },
    bullets: [
      { ru: 'Освоено, шатко, не начато', en: 'Mastered, shaky, not started' },
      { ru: 'Повторяющиеся ошибки как сигнал', en: 'Repeated errors as a signal' },
      { ru: 'Обновляется после каждого шага', en: 'Updated after every step' },
    ],
  },
  {
    title: { ru: 'Следующий доступный шаг', en: 'Next available step' },
    text: {
      ru: 'Система выбирает следующий оптимальный шаг на основе текущего состояния ученика и цели учителя.',
      en: 'The system selects the next optimal step from the learner’s current state and the teacher’s goal.',
    },
    bullets: [
      { ru: 'Достаточно сложный, чтобы развивать', en: 'Challenging enough to grow' },
      { ru: 'Доступный для успешного продвижения', en: 'Reachable enough to succeed' },
      { ru: 'Своевременный и уместный', en: 'Timely and relevant' },
    ],
  },
  {
    title: { ru: 'Короткий учебный блок', en: 'Short learning block' },
    text: {
      ru: 'Короткое объяснение и одно задание вокруг выбранного шага. Ничего лишнего: только то, что нужно, чтобы сделать следующий ход.',
      en: 'A short explanation and one task around the chosen step. Nothing extra: only what is needed to make the next move.',
    },
    bullets: [
      { ru: 'Одна идея за раз', en: 'One idea at a time' },
      { ru: 'Пример, затем попытка', en: 'An example, then an attempt' },
      { ru: 'Подсказка не заменяет решение', en: 'A hint does not replace the solution' },
    ],
  },
  {
    title: { ru: 'Анализ ответа', en: 'Response analysis' },
    text: {
      ru: 'Важен не только ответ, но и путь к нему: где ученик остановился, что исправил, какая подсказка понадобилась.',
      en: 'Not only the answer matters but the path to it: where the learner paused, what they corrected, which hint was needed.',
    },
    bullets: [
      { ru: 'Ответ и способ рассуждения', en: 'The answer and the reasoning' },
      { ru: 'Ошибка как ориентир', en: 'The error as a guide' },
      { ru: 'Без штрафа за попытку', en: 'No penalty for trying' },
    ],
  },
  {
    title: { ru: 'Обновление карты', en: 'Map update' },
    text: {
      ru: 'Результат шага меняет карту знаний: подтверждённое закрепляется, шаткое остаётся в фокусе, новые связи становятся доступны.',
      en: 'The result of the step changes the knowledge map: confirmed items settle, shaky ones stay in focus, new links open up.',
    },
    bullets: [
      { ru: 'Освоение, а не количество попыток', en: 'Mastery, not attempt count' },
      { ru: 'Связи между понятиями', en: 'Links between concepts' },
      { ru: 'Готово к следующему выбору', en: 'Ready for the next choice' },
    ],
  },
  {
    title: { ru: 'Сигнал учителю', en: 'Teacher signal' },
    text: {
      ru: 'Учитель видит не таблицу баллов, а причину и предлагаемое действие. Решение за ним: принять, изменить или отложить.',
      en: 'The teacher sees a cause and a suggested action, not a score table. The decision is theirs: accept, change or postpone.',
    },
    bullets: [
      { ru: 'Причина, а не только оценка', en: 'A cause, not only a score' },
      { ru: 'Основание видно целиком', en: 'The evidence is fully visible' },
      { ru: 'Действие остаётся за учителем', en: 'The action stays with the teacher' },
    ],
  },
  {
    title: { ru: 'Коррекция маршрута', en: 'Route correction' },
    text: {
      ru: 'Маршрут перестраивается по свежему наблюдению: сложность, формат и объём поддержки меняются, и цикл начинается снова.',
      en: 'The route is rebuilt from the newest observation: difficulty, format and support change, and the cycle begins again.',
    },
    bullets: [
      { ru: 'Следующий шаг точнее предыдущего', en: 'The next step is more precise' },
      { ru: 'Поддержка убывает по мере роста', en: 'Support fades as skill grows' },
      { ru: 'Цикл не обрывается после урока', en: 'The cycle does not end with the lesson' },
    ],
  },
];

/** Node centres, identical to reference/hotspots.ts (cycle-N). */
const CENTERS: readonly (readonly [number, number])[] = [
  [695, 263], [922, 263], [1063, 395], [1047, 559], [920, 660], [697, 661], [563, 560], [563, 395],
];
const NODE = 88;
const PAINTED_ACTIVE = 2;

interface PaintedLabel {
  /** Tight cover around the painted caption; never touches a node disc. */
  rect: Rect;
  /** Background sampled next to the caption. */
  bg: string;
  caption: Bilingual;
  sub: Bilingual;
  /** Extra plain covers for painted tails that the main rect cannot reach without touching a node. */
  patches?: readonly Rect[];
}

const LABELS: readonly PaintedLabel[] = [
  { rect: [548, 162, 128, 62], bg: '#050c14', caption: { ru: 'ЦЕЛЬ УЧИТЕЛЯ', en: 'TEACHER GOAL' }, sub: { ru: 'Чему и зачем\nмы учим', en: 'What we teach\nand why' } },
  { rect: [964, 162, 172, 68], bg: '#050d15', caption: { ru: 'ТЕКУЩЕЕ СОСТОЯНИЕ', en: 'CURRENT STATE' }, sub: { ru: 'Что уже знает\nи умеет ученик', en: 'What the learner\nalready knows' } },
  { rect: [1107, 359, 168, 92], bg: '#08131d', caption: { ru: 'СЛЕДУЮЩИЙ\nДОСТУПНЫЙ ШАГ', en: 'NEXT\nAVAILABLE STEP' }, sub: { ru: 'Выбираем оптимальный\nшаг для роста', en: 'Choosing the best\nstep to grow' } },
  { rect: [1097, 562, 141, 90], bg: '#0a161f', caption: { ru: 'КОРОТКИЙ\nУЧЕБНЫЙ БЛОК', en: 'SHORT\nLEARNING BLOCK' }, sub: { ru: 'Небольшая порция\nнового знания', en: 'A small portion\nof new knowledge' } },
  { rect: [923, 707, 176, 66], bg: '#060f16', caption: { ru: 'АНАЛИЗ ОТВЕТА', en: 'RESPONSE ANALYSIS' }, sub: { ru: 'Понимаем, что получилось,\nа что нет', en: 'Seeing what worked\nand what did not' } },
  { rect: [575, 711, 177, 68], bg: '#060d16', caption: { ru: 'ОБНОВЛЕНИЕ КАРТЫ', en: 'MAP UPDATE' }, sub: { ru: 'Уточняем модель знаний\nученика', en: 'Refining the learner’s\nknowledge model' } },
  { rect: [384, 559, 134, 74], bg: '#081018', caption: { ru: 'СИГНАЛ УЧИТЕЛЮ', en: 'TEACHER SIGNAL' }, sub: { ru: 'Данные для решения\nи поддержки', en: 'Data for a decision\nand support' }, patches: [[518, 587, 12, 44]] },
  { rect: [339, 390, 172, 68], bg: '#070e16', caption: { ru: 'КОРРЕКЦИЯ МАРШРУТА', en: 'ROUTE CORRECTION' }, sub: { ru: 'Изменяем путь,\nчтобы двигаться дальше', en: 'Changing the path\nto keep moving' } },
];

const DETAIL: Rect = [1283, 187, 344, 600];
const CONTROLLER: Rect = [497, 829, 678, 68];
const MUTE: Rect = [CENTERS[PAINTED_ACTIVE][0] - 52, CENTERS[PAINTED_ACTIVE][1] - 52, 104, 104];

/** True once the inset state changed after mount, so enter animations never run on scene mount. */
function useLiveFlag(key: string): boolean {
  const first = useRef(key);
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!live && key !== first.current) setLive(true);
  }, [key, live]);
  return live;
}

function ProgressRing({ fraction }: { fraction: number }) {
  const r = 46;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" className={styles.ringSvg} aria-hidden="true">
      <circle cx="50" cy="50" r={r} className={styles.ringTrack} />
      <circle cx="50" cy="50" r={r} className={styles.ringArc} strokeDasharray={`${c * fraction} ${c}`} />
    </svg>
  );
}

function Glyph({ kind }: { kind: 'pause' | 'play' | 'next' | 'bulb' | 'spark' | 'steps' }) {
  switch (kind) {
    case 'pause':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14" /></svg>;
    case 'play':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l10 7-10 7z" /></svg>;
    case 'next':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5l10 7-10 7zM18 5v14" /></svg>;
    case 'bulb':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18h6M10 21h4M8.5 14.5a5.5 5.5 0 1 1 7 0c-.9.8-1.5 1.7-1.5 2.5h-4c0-.8-.6-1.7-1.5-2.5z" /></svg>;
    case 'spark':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12h12M11 7l5 5-5 5" /></svg>;
    case 'steps':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19h4v-4h4v-4h4V7h4M14 7h6v6" /></svg>;
  }
}

export default function LearningCycleInsets({ lang, state, update, openAssistant, spotlight, onInteraction }: InsetProps) {
  const living = useContext(LivingContext);
  const reduced = living?.reducedMotion ?? false;
  const ru = lang === 'ru';
  const step = ((state.cycleStep % STEPS.length) + STEPS.length) % STEPS.length;
  const [paused, setPaused] = useState(false);
  const live = useLiveFlag(`${step}:${paused}`);
  const info = STEPS[step];
  const pick = (copy: Bilingual) => (ru ? copy.ru : copy.en);

  const labels = {
    panel: ru ? 'Текущий шаг цикла' : 'Current cycle step',
    eyebrow: ru ? 'ТЕКУЩИЙ ШАГ' : 'CURRENT STEP',
    detail: ru ? 'Подробности текущего шага' : 'Current step details',
    note: ru
      ? 'Мы не обещаем, что одного урока будет достаточно. Мы строим путь, который работает.'
      : 'We do not promise that one lesson is enough. We build a path that works.',
    pause: paused ? (ru ? 'ПРОДОЛЖИТЬ' : 'RESUME') : (ru ? 'ПАУЗА' : 'PAUSE'),
    pauseName: paused ? (ru ? 'Продолжить цикл' : 'Resume the cycle') : (ru ? 'Приостановить цикл' : 'Pause the cycle'),
    status: paused ? (ru ? 'ЦИКЛ НА ПАУЗЕ' : 'CYCLE PAUSED') : (ru ? 'ЦИКЛ АКТИВЕН' : 'CYCLE ACTIVE'),
    next: ru ? 'СЛЕДУЮЩИЙ ЦИКЛ' : 'NEXT CYCLE',
    nextName: ru ? 'Следующий цикл' : 'Next cycle',
    controller: ru ? 'Управление циклом' : 'Cycle controls',
    ring: ru ? `Шаг ${step + 1} из ${STEPS.length}` : `Step ${step + 1} of ${STEPS.length}`,
  };

  const press = (label: string, action: () => void) => () => {
    onInteraction?.(label);
    action();
  };

  const selectedLabel = LABELS[step];
  const painted = LABELS[PAINTED_ACTIVE];
  const showLabelCovers = step !== PAINTED_ACTIVE;

  return (
    <div className={styles.root} data-live={live ? 'true' : undefined} data-reduced={reduced ? 'true' : undefined}>
      {/* Selected-step ring over the node; the raster hotspot underneath keeps the click. */}
      <span
        className={styles.nodeRing}
        style={at([CENTERS[step][0] - NODE / 2, CENTERS[step][1] - NODE / 2, NODE, NODE])}
        aria-hidden="true"
      />

      {showLabelCovers && (
        <>
          <span className={styles.mute} style={at(MUTE)} aria-hidden="true">
            <span className={styles.muteDisc}><Glyph kind="steps" /></span>
          </span>
          {painted.patches?.map((patch, index) => (
            <span key={`p${index}`} className={styles.patch} style={{ ...at(patch), background: painted.bg }} aria-hidden="true" />
          ))}
          <span className={styles.label} style={{ ...at(painted.rect), background: painted.bg }} aria-hidden="true">
            <b><i>{PAINTED_ACTIVE + 1}.</i><span>{pick(painted.caption)}</span></b>
            <small>{pick(painted.sub)}</small>
          </span>
          {selectedLabel.patches?.map((patch, index) => (
            <span key={`s${index}`} className={styles.patch} style={{ ...at(patch), background: selectedLabel.bg }} aria-hidden="true" />
          ))}
          <span key={step} className={`${styles.label} ${styles.labelActive}`} style={{ ...at(selectedLabel.rect), background: selectedLabel.bg }} aria-hidden="true">
            <b><i>{step + 1}.</i><span>{pick(selectedLabel.caption)}</span></b>
            <small>{pick(selectedLabel.sub)}</small>
          </span>
        </>
      )}

      <section className={styles.panel} style={at(DETAIL)} aria-label={labels.panel}>
        <div key={`body-${step}`} className={styles.body}>
          <span className={styles.ring} style={stagger(0)} role="img" aria-label={labels.ring}>
            <ProgressRing fraction={(step + 1) / STEPS.length} />
            <em>{String(step + 1).padStart(2, '0')}</em>
          </span>
          <p className={styles.eyebrow} style={stagger(1)}>{labels.eyebrow}</p>
          <h3 className={styles.title} style={stagger(2)}>{step + 1}. {pick(info.title)}</h3>
          <i className={styles.rule} style={stagger(3)} aria-hidden="true" />
          <p className={styles.text} style={stagger(4)}>{pick(info.text)}</p>
          <ul className={styles.bullets} style={stagger(5)}>
            {info.bullets.map((bullet) => <li key={bullet.en}>{pick(bullet)}</li>)}
          </ul>
        </div>
        <button
          type="button"
          className={styles.note}
          data-hotspot="cycle-detail"
          data-spotlight={spotlight === 'cycle-detail' ? 'true' : undefined}
          aria-label={labels.detail}
          onClick={press(labels.detail, () => openAssistant(ru ? 'Объясни текущий шаг цикла' : 'Explain the current cycle step'))}
        >
          <span className={styles.noteIcon}><Glyph kind="bulb" /></span>
          <span className={styles.noteText}>{labels.note}</span>
          {spotlight === 'cycle-detail' && <i className={styles.pulse} aria-hidden="true" />}
        </button>
        {live && <i key={`relight-${step}`} className={styles.relight} aria-hidden="true" />}
      </section>

      <div className={styles.controller} style={at(CONTROLLER)} role="group" aria-label={labels.controller}>
        <span className={styles.spark} aria-hidden="true"><Glyph kind="spark" /></span>
        <button
          type="button"
          className={styles.control}
          style={{ left: '4.1%', width: '15.2%' }}
          data-hotspot="cycle-pause"
          data-spotlight={spotlight === 'cycle-pause' ? 'true' : undefined}
          aria-pressed={paused}
          aria-label={labels.pauseName}
          onClick={press(labels.pauseName, () => setPaused((value) => !value))}
        >
          <span className={styles.controlIcon}><Glyph kind={paused ? 'play' : 'pause'} /></span>
          <span className={styles.controlLabel}>{labels.pause}</span>
          {spotlight === 'cycle-pause' && <i className={styles.pulse} aria-hidden="true" />}
        </button>
        <div key={String(paused)} className={`${styles.status} ${paused ? styles.statusPaused : ''}`} aria-live="polite">
          <svg viewBox="0 0 34 34" className={styles.statusRing} aria-hidden="true">
            <circle cx="17" cy="17" r="14" />
          </svg>
          <span>{labels.status}</span>
        </div>
        <button
          type="button"
          className={styles.control}
          style={{ left: '63%', width: '30.8%' }}
          data-hotspot="cycle-next"
          data-spotlight={spotlight === 'cycle-next' ? 'true' : undefined}
          aria-label={labels.nextName}
          onClick={press(labels.nextName, () => update({ cycleStep: (step + 1) % STEPS.length }))}
        >
          <span className={styles.controlIcon}><Glyph kind="next" /></span>
          <span className={styles.controlLabel}>{labels.next}</span>
          {spotlight === 'cycle-next' && <i className={styles.pulse} aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}
